import React from 'react';
import { Box, Typography } from '@mui/material';
import { Screen } from 'src/components/screen';
import {
  contentContainer,
  logoImage,
  paragraph,
  root,
  subtitle,
  title,
} from './styles';
import { useTranslation } from 'src/text';
import logo from 'src/assets/images/logo.png';

const PrivacyPolicyScreen: React.FC = () => {
  const updated = new Date().toLocaleDateString();
  const { genericLabels } = useTranslation();

  return (
    <Screen contentContainerStyle={root}>
      <Box sx={contentContainer}>
        <img src={logo} alt="Logo" style={logoImage} />

        <Typography variant="h5" sx={title}>
          {genericLabels.privacy}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Last updated: {updated}
        </Typography>

        <Typography variant="h6" sx={subtitle}>
          Overview
        </Typography>
        <Typography sx={paragraph}>
          This Privacy Policy explains how Neberox (&quot;we&quot;,
          &quot;us&quot;) collects, uses, and shares information when you use
          Acuverity Toolkit and its services (&quot;Service&quot;). It also
          describes your choices and rights. By using the Service, you agree to
          this Policy.
        </Typography>

        <Typography variant="h6" sx={subtitle}>
          Information We Collect
        </Typography>
        <Typography sx={paragraph}>
          • Account and Profile Information: name, email address, profile image
          provided by your sign-in provider (e.g., Google).
          <br />
          • Content You Provide: URLs, prompts, text, campaign configuration,
          and any content you upload or paste to generate outputs.
          <br />
          • Generated Content: AI-generated captions, hashtags, and related
          output you choose to save or export.
          <br />
          • Usage and Device Data: app interactions, approximate location,
          device/browser type, and technical logs for diagnostics and security.
          <br />• Cookies and Similar Technologies: used for session management,
          authentication, and improving the Service.
        </Typography>

        <Typography variant="h6" sx={subtitle}>
          How We Use Information
        </Typography>
        <Typography sx={paragraph}>
          We use your information to:
          <br />
          • Provide, maintain, and improve the Service (including AI-driven
          features such as social caption generation).
          <br />
          • Personalize content, remember preferences, and support workflows.
          <br />
          • Communicate with you about updates, security, and support.
          <br />
          • Monitor performance, debug issues, and prevent abuse.
          <br />• Comply with legal obligations and enforce our Terms of Use.
        </Typography>

        <Typography variant="h6" sx={subtitle}>
          AI Features and Disclosures
        </Typography>
        <Typography sx={paragraph}>
          The Service uses third-party AI providers (e.g., Google Generative AI
          &quot;Gemini&quot;). Prompts and generated outputs may be sent to
          these providers for processing. AI-generated content can be inaccurate
          or incomplete. You are responsible for reviewing outputs before use
          and ensuring compliance with applicable laws, platform policies, and
          your own standards.
        </Typography>

        <Typography variant="h6" sx={subtitle}>
          Legal Bases (EEA/UK)
        </Typography>
        <Typography sx={paragraph}>
          If you are in the EEA/UK, we process personal data based on: (i)
          performance of a contract (to provide the Service), (ii) legitimate
          interests (to secure and improve the Service), (iii) consent (where
          required, e.g., certain cookies/marketing), and (iv) compliance with
          legal obligations.
        </Typography>

        <Typography variant="h6" sx={subtitle}>
          Sharing and Service Providers
        </Typography>
        <Typography sx={paragraph}>
          We do not sell your personal data. We share information with service
          providers solely to operate the Service, including hosting, storage,
          authentication, and AI processing. Typical processors include Google
          Cloud/Firebase (hosting, authentication, storage) and Google
          Generative AI (prompt processing). We may also share information to
          comply with law, protect safety, or in connection with a merger or
          acquisition.
        </Typography>

        <Typography variant="h6" sx={subtitle}>
          International Transfers
        </Typography>
        <Typography sx={paragraph}>
          Your information may be processed in countries other than your own.
          Where required, we implement appropriate safeguards for cross-border
          transfers (e.g., Standard Contractual Clauses).
        </Typography>

        <Typography variant="h6" sx={subtitle}>
          Data Retention
        </Typography>
        <Typography sx={paragraph}>
          We retain information for as long as necessary to provide the Service
          and for legitimate business or legal purposes. Retention periods vary
          based on data type and usage. You may request deletion of certain
          data, subject to exceptions.
        </Typography>

        <Typography variant="h6" sx={subtitle}>
          Security
        </Typography>
        <Typography sx={paragraph}>
          We implement reasonable administrative, technical, and physical
          safeguards designed to protect your information. However, no method of
          transmission or storage is 100% secure, and we cannot guarantee
          absolute security.
        </Typography>

        <Typography variant="h6" sx={subtitle}>
          Your Rights and Choices
        </Typography>
        <Typography sx={paragraph}>
          Depending on your location, you may have rights to access, correct,
          delete, or port your personal data, and to object to or restrict
          certain processing. You can exercise rights by contacting
          info@neberox.com. You may also control cookies via browser settings.
        </Typography>

        <Typography variant="h6" sx={subtitle}>
          Children’s Privacy
        </Typography>
        <Typography sx={paragraph}>
          The Service is not intended for children under 13 (or the equivalent
          age of consent in your jurisdiction). We do not knowingly collect
          personal data from children. If you believe a child has provided us
          data, contact us for removal.
        </Typography>

        <Typography variant="h6" sx={subtitle}>
          Changes to this Policy
        </Typography>
        <Typography sx={paragraph}>
          We may update this Policy from time to time. Material changes will be
          indicated by updating the &quot;Last updated&quot; date and, where
          appropriate, additional notice.
        </Typography>

        <Typography variant="h6" sx={subtitle}>
          Contact
        </Typography>
        <Typography sx={paragraph}>
          Questions or requests? Contact us at info@neberox.com.
        </Typography>
      </Box>
    </Screen>
  );
};

export { PrivacyPolicyScreen };
